import { Button } from "@pepperstone/pepper-ui-kit";
import { useMemo, useState } from "react";

import type { DropdownOption } from "../../../DropDownV2";
import type { ClientAccountTableFilterSubProps } from "./types";

import { DropDownV2 } from "../../../DropDownV2";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { MultiSelect } from "../../../MultiSelect";
import { SearchInput } from "../../../SearchInput";
import { useClientAccountReportContext } from "../providers/AccountReportProvider";

export const MobileFilters = ({
  handleExportButton,
  handleSearchInput,
  handleRebateAccountsDropdown,
  handleRebateProgramsMultiSelect,
  canViewPII,
  redactedFields,
  translations: { translatedContent },
  showRebateProgramFilter,
}: ClientAccountTableFilterSubProps) => {
  const isMobile = useIsMobile();

  const translatedLabels = useMemo(
    () => ({
      selectAccountLabel: translatedContent.dropDownLabels?.selectLabel ?? "",
      allItemsSelectedLabel:
        translatedContent.dropDownLabels?.allItemsSelectedLabel ?? "",
      itemsSelectedLabel:
        translatedContent.dropDownLabels?.itemsSelectedLabel ?? "",
      clearSelectionLabel:
        translatedContent.dropDownLabels?.clearSelectionLabel ?? "",
      selectAllLabel: translatedContent.dropDownLabels?.selectAllLabel ?? "",
      applyButtonLabel:
        translatedContent.dropDownLabels?.applyButtonLabel ?? "",
    }),
    [
      translatedContent.dropDownLabels?.allItemsSelectedLabel,
      translatedContent.dropDownLabels?.applyButtonLabel,
      translatedContent.dropDownLabels?.clearSelectionLabel,
      translatedContent.dropDownLabels?.itemsSelectedLabel,
      translatedContent.dropDownLabels?.selectAllLabel,
      translatedContent.dropDownLabels?.selectLabel,
    ],
  );

  const {
    state: {
      searchQuery,
      selected: {
        rebateAccountIDs: selectedRebateAccountIDs,
        rebateProgramIDs: selectedRebateProgramIDs,
      },
    },
    options: { rebateAccountIDs, rebateProgramIDs },
  } = useClientAccountReportContext();

  const [search, setSearch] = useState(searchQuery);

  const selectedRebateAccounts = useMemo(() => {
    return ((selectedRebateAccountIDs as string[]) ?? []).map((id) => ({
      label:
        rebateAccountIDs.find((option) => option.value === id)?.label || id,
      value: id,
    }));
  }, [rebateAccountIDs, selectedRebateAccountIDs]);

  const selectedRebatePrograms = useMemo(() => {
    return ((selectedRebateProgramIDs as string[]) ?? []).map((id) => ({
      label:
        rebateProgramIDs.find((option) => option.value === id)?.label || id,
      value: id,
    }));
  }, [rebateProgramIDs, selectedRebateProgramIDs]);

  const handleDropdownChange = (options: DropdownOption[]) => {
    handleRebateAccountsDropdown?.(options);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    handleSearchInput?.(value);
  };

  const searchTitle = useMemo(() => {
    if (canViewPII) {
      if (redactedFields?.includes("name")) {
        return translatedContent?.filters?.searchWithPIIEmail;
      }
      if (redactedFields?.includes("email")) {
        return translatedContent?.filters?.searchWithPIIName;
      }
      return translatedContent?.filters?.search;
    }
    return translatedContent?.filters?.searchNoPII;
  }, [canViewPII, redactedFields, translatedContent]);

  return (
    <div data-testid="mobile-filters" className="mt-4 flex flex-col gap-4">
      <div>
        <DropDownV2
          id="mobileSearchDropdown"
          options={rebateAccountIDs}
          selected={selectedRebateAccounts}
          label={translatedContent.filters?.rebateAccounts}
          isMobile={isMobile}
          dropDownLabels={translatedLabels}
          handleApply={handleDropdownChange}
        />
      </div>
      {showRebateProgramFilter && (
        <div>
          <MultiSelect
            id="mobileRebateProgramFilter"
            options={rebateProgramIDs}
            selected={selectedRebatePrograms}
            label={translatedContent.filters?.rebatePrograms}
            placeholder={translatedContent.filters?.rebateProgramsPlaceholder}
            onChange={(options) => handleRebateProgramsMultiSelect?.(options)}
          />
        </div>
      )}
      <div>
        <SearchInput
          label={translatedContent.searchLabel}
          value={search}
          onChange={handleSearchChange}
          placeholder={searchTitle}
        />
      </div>
      <div className="flex h-[72px] flex-row items-end justify-end">
        <Button
          as="button"
          type="button"
          size="ps-btn-md"
          variant="ps-btn-secondary"
          onClick={handleExportButton}
        >
          {translatedContent.exportCSVText}
        </Button>
      </div>
    </div>
  );
};
